import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, switchMap, tap } from 'rxjs';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class PatientTaskService {
  private apiUrl = `api/tasks`;
  private patientTasksSubject = new BehaviorSubject<any[] | null>(null);
  private editingTaskSubject = new BehaviorSubject<any>(null);

  patientTasks$ = this.patientTasksSubject.asObservable();
  editingTask$ = this.editingTaskSubject.asObservable();

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          this.patientTasksSubject.next([]);
          return this.loadTasksByPatient(patient.id);
        } else {
          // If there's no current patient, clear the tasks
          this.patientTasksSubject.next([]);
          return []; // Return an empty array to satisfy the Observable return type
        }
      }),
      tap((results: any) => {
        // Update the BehaviorSubject with the new or cleared tasks
        const tasks = results ? results.data : [];
        this.patientTasksSubject.next(tasks);
      })
    ).subscribe();
  }

  private setupSocketListeners(): void {

    // Current patient's ID
    let currentPatientId: string | null = null;

    // Subscribe to the current patient changes to keep track of the current patient's ID
    this.patientService.currentPatient$.subscribe(patient => {
      currentPatientId = patient ? patient.id : null;
    });

    // Listener for when a new task is added
    this.socketService.listen('taskAdded', 'patient-task-service').subscribe(newInfo => {
      if (newInfo.patientId === currentPatientId) {
        const current = this.patientTasksSubject.getValue() || [];
        this.patientTasksSubject.next([...current!, newInfo]);
      }
    });

    // Listener for when a task is updated
    this.socketService.listen('taskUpdated', 'patient-task-service').subscribe(updatedInfo => {
      if (updatedInfo.patientId === currentPatientId) {
        const current = this.patientTasksSubject.getValue() || [];
        const updated = current!.map(item => item.id === updatedInfo.id ? updatedInfo : item);
        this.patientTasksSubject.next(updated);
      }
    });

    // Listener for when a task is deleted
    this.socketService.listen('taskDeleted', 'patient-task-service').subscribe(deletedInfo => {
      if (deletedInfo.patientId === currentPatientId) {
        const current = this.patientTasksSubject.getValue() || [];
        const updated = current!.filter(item => item.id !== deletedInfo.id);
        this.patientTasksSubject.next(updated);
      }
    });

  }

  loadTasksByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  addTask(taskData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(this.apiUrl, taskData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getTaskById(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateTask(id: string, data: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  deleteTask(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  setEditingTask(task: any | null) {
    this.editingTaskSubject.next(task);
  }
}
